import { isChunkLoadError } from '@shared/utils/isChunkLoadError';

// WebKit fails in-flight chunk scripts as soon as a navigation starts (script `error` right after
// `beforeunload`, or after `pagehide`). Those ChunkLoadErrors never reach the user, so reporting
// them only buries real failures.
let unloading = false;

// `beforeunload` does not always lead to an unload: a cancelled "Leave site?" prompt, or a
// mailto:/download link. So it only counts until the user acts on the page again, or for 5s at most.
// Not `focus`: it can fire when the prompt closes even if the user chose to leave.
const BEFORE_UNLOAD_GRACE_MS = 5000;
const USER_ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;
let endBeforeUnloadGrace: (() => void) | undefined;

const clearBeforeUnloadGrace = (): void => {
  endBeforeUnloadGrace?.();
  endBeforeUnloadGrace = undefined;
};

const startBeforeUnloadGrace = (target: EventTarget): void => {
  clearBeforeUnloadGrace();
  const stillHere = () => {
    clearBeforeUnloadGrace();
    unloading = false;
  };
  const timeoutId = setTimeout(stillHere, BEFORE_UNLOAD_GRACE_MS);
  USER_ACTIVITY_EVENTS.forEach(type => target.addEventListener(type, stillHere, { capture: true, passive: true }));
  endBeforeUnloadGrace = () => {
    clearTimeout(timeoutId);
    USER_ACTIVITY_EVENTS.forEach(type => target.removeEventListener(type, stillHere, { capture: true }));
  };
};

/**
 * Every browser engine on iOS is WebKit, so `CriOS`/`FxiOS` match too. Chromium browsers also say
 * `AppleWebKit` but add `Chrome/`; Firefox says neither.
 */
const isWebKit = (userAgent: string): boolean => /AppleWebKit\//.test(userAgent) && !/Chrome\/|Chromium\//.test(userAgent);

const trackedTargets = new WeakSet<EventTarget>();

/** Starts tracking whether the page is being navigated away from. Safe to call more than once. */
export const trackPageUnloading = (
  target: EventTarget | undefined = typeof window === 'undefined' ? undefined : window,
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): void => {
  if (!target || trackedTargets.has(target)) return;
  trackedTargets.add(target);

  // WebKit-only: only WebKit aborts scripts at `beforeunload`, and in Firefox a `beforeunload`
  // listener makes the page ineligible for the back/forward cache.
  if (isWebKit(userAgent)) {
    target.addEventListener('beforeunload', () => {
      unloading = true;
      startBeforeUnloadGrace(target);
    });
  }
  target.addEventListener('pagehide', () => {
    clearBeforeUnloadGrace();
    unloading = true;
  });
  // Fires when the page is restored from the back/forward cache.
  target.addEventListener('pageshow', () => {
    clearBeforeUnloadGrace();
    unloading = false;
  });
};

export const isPageUnloading = (): boolean => unloading;

/** `true` for a chunk-load failure caused by the page unloading, which should not be reported. */
export const isChunkLoadErrorWhileUnloading = (error: unknown): boolean => unloading && !!error && typeof error === 'object' && isChunkLoadError(error as Error);

const reset = (): void => {
  clearBeforeUnloadGrace();
  unloading = false;
};

// Internal exports for unit testing only.
export const __testing__ = { reset, BEFORE_UNLOAD_GRACE_MS };
